import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import clsx from 'clsx'

import { Link } from 'react-router-dom'

import { TRoles, TRoutes } from '../../shared/const'
import { shouldShowAppFooter } from '../../components/AppFooter'

import RequestForm from './RequestForm'
import useFabPosition from './useFabPosition'
import useNavigationCountdown, { WECHSEL_BIS_ZURUECK } from './useNavigationCountdown'
import styles from './style.module.scss'

/**
 * Der Hilfe-Knopf: der Weg von Schüler:innen, Lehrkräften und
 * Unterstützer:innen zu einer Anfrage (Anfragen-Konzept, Stufe 2).
 *
 * Er heißt **„Hilfe"** und nicht „Support": Das Wort ist in dieser Anwendung
 * vergeben — es gibt Unterstützer:innen und einen Unterstützungsbedarf, und ein
 * „Support"-Knopf säße bei einer Unterstützer:in neben ihrem eigenen Bereich
 * und meinte etwas anderes. Der Knopf beschreibt den Moment, in dem man ihn
 * drückt; was daraus entsteht, heißt **Anfrage**.
 *
 * **Verwaltungsrollen sehen ihn nicht** — sie haben den Menüpunkt
 * „Anforderungen" und legen dort direkt an.
 *
 * **Und nur, wo der Kanal freigeschaltet ist** (Einstellungen → Anfragen):
 * Die Freigabe hängt an Rolle *und* Unternehmensform — in einer Junior Basic
 * Company soll oft die Lehrkraft der Weg sein und nicht die Schüler:in.
 * Entschieden wird das am Server; hier zählt nur die Antwort
 * (`profile.mayCreateRequest`). Eine Lehrkraft betreut mehrere Junior
 * Companies, deren Unternehmensformen das Frontend ohne weiteren Aufruf gar
 * nicht kennt — die Regel hier nachzubauen hieße, sie falsch nachzubauen.
 *
 * **Warum er unter jedem Fenster liegt** (`z-index` kleiner als die Dialoge):
 * Unten rechts sitzt in jedem Fenster der „Speichern"-Knopf. Statt jedes offene
 * Fenster zu erkennen — es gibt mehrere Arten davon, und eine vergessene wäre
 * genau der Fall, der wehtut — liegt der Knopf einfach darunter. Kein Zustand,
 * der auseinanderlaufen kann.
 *
 * **Verschoben und weggeklickt gilt auf Zeit**: Nach fünf Bildschirmwechseln
 * steht der Knopf wieder unten rechts (useNavigationCountdown).
 */
const HELP_ROLES = [TRoles.STUDENT, TRoles.TEACHER, TRoles.SUPPORTER]

const DISMISS_KEY = 'junior.help.dismissed'

const GROESSE = { breite: 112, hoehe: 48 }

export default function HelpButton() {
	const { t } = useTranslation()
	const location = useLocation()
	const isAuth = useSelector(state => state.user.isAuth)
	const role = useSelector(state => state.user.role)
	// In der Schüler:innen-Ansicht der Lehrkraft ist die Anwendung geborgt —
	// eine Anfrage von dort wäre der falschen Person zugeordnet.
	const isTeacherStudentView = useSelector(state => state.user.isTeacherStudentView)
	/*
	 * Fehlt die Angabe — ein Profil aus der Zeit vor dieser Änderung, eine
	 * Antwort, die noch unterwegs ist —, gilt der Knopf als freigeschaltet.
	 * Ihn im Zweifel wegzunehmen hieße, einen bestehenden Weg an einem
	 * Ladezustand scheitern zu lassen.
	 */
	const darfAnfragen = useSelector(state => state.user.profile.mayCreateRequest !== false)

	const [offen, setOffen] = useState(false)
	const { wert: ausgeblendet, starte: verstecke } = useNavigationCountdown(DISMISS_KEY)
	const { position, ziehtGerade, zuruecksetzen, warZiehen, handlers } = useFabPosition(GROESSE)

	/*
	 * Wegklicken räumt zugleich eine verschobene Lage ab: Wer den Knopf
	 * wiederkommen sieht, soll ihn dort sehen, wo er hingehört — unten rechts.
	 */
	const wegklicken = () => {
		verstecke()
		zuruecksetzen()
	}

	const darfMelden =
		isAuth &&
		!isTeacherStudentView &&
		darfAnfragen &&
		HELP_ROLES.includes(role) &&
		shouldShowAppFooter(isAuth, location.pathname)

	if (!darfMelden) return null

	return (
		<>
			{!ausgeblendet && (
				<div
					className={clsx(
						styles.root,
						// Ohne eigene Lage hängt er per CSS unten rechts — siehe
						// useFabPosition.
						!position && styles.defaultCorner,
						ziehtGerade && styles.dragging
					)}
					style={position ? { left: position.x, top: position.y } : undefined}
				>
					<button
						type="button"
						className={styles.button}
						aria-label={t('Help and feedback')}
						title={t('Submit a request')}
						onClick={() => {
							// Nach dem Verschieben kommt noch ein Klick — der
							// gehört zum Ziehen und nicht zum Öffnen.
							if (!warZiehen()) setOffen(true)
						}}
						{...handlers}
					>
						<svg className={styles.icon} viewBox="0 0 20 20" aria-hidden="true">
							<circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
							<path
								d="M7.4 7.6a2.6 2.6 0 1 1 3.4 2.5c-.6.2-.9.7-.9 1.3v.5"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.6"
								strokeLinecap="round"
							/>
							<circle cx="9.9" cy="14.4" r="1" fill="currentColor" />
						</svg>
						<span className={styles.label}>{t('Help')}</span>
					</button>

					{/*
					 * Der Weg zurueck zu den eigenen Vorgaengen. Er steht am Knopf
					 * und nicht nur in der Navigation: Wer eine Anfrage gestellt
					 * hat, sucht sie dort, wo er sie abgeschickt hat.
					 */}
					<Link
						to={TRoutes.MY_REQUESTS}
						className={styles.mine}
						title={t('My requests')}
						aria-label={t('My requests')}
					>
						<svg viewBox="0 0 12 12" aria-hidden="true">
							<path
								d="M2 2.5h8M2 6h8M2 9.5h5"
								stroke="currentColor"
								strokeWidth="1.4"
								strokeLinecap="round"
							/>
						</svg>
					</Link>

					<button
						type="button"
						className={styles.dismiss}
						aria-label={t('Hide the help button')}
						title={t('Hide — it comes back after {{val1}} page changes', {
							val1: WECHSEL_BIS_ZURUECK
						})}
						onClick={wegklicken}
					>
						<svg viewBox="0 0 10 10" aria-hidden="true">
							<path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
						</svg>
					</button>
				</div>
			)}

			{offen && <RequestForm onClose={() => setOffen(false)} />}
		</>
	)
}
